/**
 * Market label formatting utilities
 *
 * Provides centralized formatting for bucket/outcome display labels
 * based on market type and position context.
 *
 * Used by both backend (to compute labels once) and frontend (for display).
 */

type MarketType = "binary" | "fall_from_grace"

interface FormatBucketLabelArgs {
	/** Type of market */
	marketType: MarketType
	/** Starting rank for binary markets (1-20), null for FFG */
	startRank: number | null
	/** Creator name to include in label */
	creatorName: string
	/** Bucket index (0 = first bucket, 1 = second bucket, etc.) */
	bucketIndex: number
	/** Base label (e.g., "Long", "Short", or creator name for FFG) */
	baseLabel: string
	/** When true, return baseLabel as-is without rank-based formatting (for custom question markets) */
	useRawLabel?: boolean
}

/**
 * Formats a bucket label for display based on market type and context.
 *
 * Note: "next week" is in the market title, not the bucket labels.
 *
 * - FFG markets: "[name] falls most"
 * - Binary rank 1: "[name] holds the crown" / "Short [name]"
 * - Binary rank 20: "Long [name]" / "[name] eliminated"
 * - Binary rank 2-19: "Long [name]" / "Short [name]"
 */
export function formatBucketDisplayLabel(args: FormatBucketLabelArgs): string {
	const { marketType, startRank, creatorName, bucketIndex, baseLabel, useRawLabel } = args

	// Custom question markets: use the label directly (e.g., "Yes" / "No")
	if (useRawLabel) {
		return baseLabel
	}

	// Fall from Grace: "[name] falls most"
	if (marketType === "fall_from_grace") {
		return `${baseLabel} falls most`
	}

	// Binary markets
	const isLong = bucketIndex === 0

	// Rank 1: special "holds the crown" / "Short X"
	if (startRank === 1) {
		return isLong ? `${creatorName} holds the crown` : `Short ${creatorName}`
	}

	// Rank 20: special "Long X" / "X eliminated"
	if (startRank === 20) {
		return isLong ? `Long ${creatorName}` : `${creatorName} eliminated`
	}

	// Ranks 2-19: "Long/Short [name]"
	return isLong ? `Long ${creatorName}` : `Short ${creatorName}`
}
